import { allOrdersPath, userOrdersPathDate } from "../../functions/src/admin/paths";
import { normalizeOrderContent, orderHasContentLines } from "../../functions/src/common/orders";
import type { Order } from "../../functions/src/types";
import { schoolDatabase } from "../api/firebase";

/** `isChangingOrder` draft differs from the committed placed order (staged for basket checkout). */
export function hasUnsubmittedOrderChanges(
    isChangingOrder: boolean,
    draft: Order | null | undefined,
    placed: Order | null | undefined,
): boolean {
    if (!isChangingOrder || !draft || !placed) {
        return false;
    }
    return normalizeOrderContent(draft) !== normalizeOrderContent(placed);
}

/** Change mode with all lines removed — not a valid change; user must cancel via Cancel my Order. */
export function isChangeOrderEmptyDraft(
    isChangingOrder: boolean,
    draft: Order | null | undefined,
    placed: Order | null | undefined,
): boolean {
    if (!isChangingOrder || !draft || !placed) {
        return false;
    }
    if (orderHasContentLines(draft)) {
        return false;
    }
    return hasUnsubmittedOrderChanges(true, draft, placed);
}

/**
 * Leave change mode for a day when there is nothing valid to keep in the draft:
 * no edits yet (draft matches placed), or an empty draft (cancel must use Cancel my Order).
 * Same outcome as Cancel Changes — restores completed UI on the strip.
 */
export async function endIdleChangeOrderModeForDay(uid: string, date: string): Promise<void> {
    if (!uid || !date) {
        return;
    }

    const orderRef = schoolDatabase.ref(userOrdersPathDate(uid, date));
    const placedRef = schoolDatabase.ref(allOrdersPath(uid, date));
    const [draftSnap, placedSnap] = await Promise.all([orderRef.once("value"), placedRef.once("value")]);
    const draft = draftSnap.val() as Order | null;
    const placed = placedSnap.val() as Order | null;

    if (!draft?.isChangingOrder) {
        return;
    }

    const hasStagedChange = hasUnsubmittedOrderChanges(true, draft, placed)
        && !isChangeOrderEmptyDraft(true, draft, placed);
    if (hasStagedChange) {
        return;
    }

    await orderRef.child("isChangingOrder").remove();
    if (placed) {
        await orderRef.set(placed);
    } else {
        await orderRef.remove();
    }
}
